import React from 'react';
import {
  Box,
  Button,
  HeadingText,
  Modal,
  ParagraphText,
  StackLayout,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import deactivateAccountIllustration from 'assets/im-deactivate-account.svg';

interface DeactivateAccountDialogProps {
  onClose: () => void;
  onConfirm: () => void;
}

export const DeactivateAccountDialog = ({
  onClose,
  onConfirm,
}: DeactivateAccountDialogProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  return (
    <Modal.Root
      open
      onOpenChange={(open: boolean) => {
        if (!open) onClose();
      }}
    >
      <Modal.Content
        width={{
          '@initial': '716px',
          '@mobile': '100%',
          '@mobileLandscape': '100%',
        }}
        showCloseButton={false}
        padding="$none"
      >
        {/* Header: centered title + close icon */}
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: 44,
            paddingInline: '$half',
          }}
        >
          <Modal.Title asChild>
            <HeadingText
              size="sm"
              level="2"
              css={{ color: '$onSurfaceTextPrimary' }}
            >
              {formatMessage({ id: 'STR_DEACTIVATE_ACCOUNT' })}
            </HeadingText>
          </Modal.Title>
          <Modal.Close asChild>
            <Button
              priority="tertiary"
              icon="tinyClose"
              hideLabel
              size="small"
              css={{
                position: 'absolute',
                right: '$half',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '$interactiveActionPrimary',
                borderRadius: 0,
                border: 'none',
              }}
            >
              {formatMessage({ id: 'STR_DISMISS' })}
            </Button>
          </Modal.Close>
        </Box>
        {/* Body */}
        <Box css={{ padding: '$oneAndHalf' }}>
          <StackLayout spacing="$one">
            <Box
              as="img"
              src={deactivateAccountIllustration}
              alt=""
              width={176}
              height={176}
              css={{ alignSelf: 'center', marginBottom: '$one' }}
            />
            <HeadingText
              size="md"
              level="3"
              css={{ color: '$onSurfaceTextPrimary' }}
            >
              {formatMessage({ id: 'STR_ARE_YOU_SURE' })}
            </HeadingText>
            <StackLayout spacing="$none">
              <ParagraphText
                css={{ '&&': { marginTop: 0, marginBottom: '$half' } }}
              >
                {formatMessage({ id: 'STR_DEACTIVATING_YOUR_ACCOUNT_WILL' })}
              </ParagraphText>
              <Box
                as="ul"
                css={{
                  paddingLeft: '$oneAndHalf',
                  margin: 0,
                  listStyleType: 'disc',
                }}
              >
                <Box as="li">
                  <ParagraphText>
                    {formatMessage({ id: 'STR_DEACTIVATE_ACCOUNT_BULLET_1' })}
                  </ParagraphText>
                </Box>
                <Box as="li">
                  <ParagraphText>
                    {formatMessage({ id: 'STR_DEACTIVATE_ACCOUNT_BULLET_2' })}
                  </ParagraphText>
                </Box>
              </Box>
            </StackLayout>
            <Box
              css={{
                display: 'flex',
                flexDirection: isMobile ? 'column-reverse' : 'row',
                justifyContent: 'flex-end',
                gap: '$one',
                marginTop: '$two',
                width: '100%',
              }}
            >
              <Button
                type="button"
                priority="secondary"
                width={isMobile ? 'fillContainer' : 'hugContents'}
                onClick={onClose}
              >
                {formatMessage({ id: 'STR_NEVER_MIND' })}
              </Button>
              <Button
                type="button"
                priority="primary"
                width={isMobile ? 'fillContainer' : 'hugContents'}
                onClick={onConfirm}
                css={{
                  '&&': {
                    backgroundColor: '$primaryBackgroundCriticalDefault',
                    borderColor: '$primaryBackgroundCriticalDefault',
                    color: '$primaryTextCriticalDefault',
                    '&:hover': {
                      backgroundColor: '$primaryBackgroundCriticalHovered',
                      borderColor: '$primaryBackgroundCriticalHovered',
                    },
                    '&:active': {
                      backgroundColor: '$primaryBackgroundCriticalPressed',
                      borderColor: '$primaryBackgroundCriticalPressed',
                    },
                  },
                }}
              >
                {formatMessage({ id: 'STR_YES_DEACTIVATE_ACCOUNT' })}
              </Button>
            </Box>
          </StackLayout>
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
};
